const errorMap: Array<{ pattern: string; code: string; message: string }> = [
  {
    // Matches domain.ErrConversationExceedsContext on the backend, which the
    // agent SSE stream sends verbatim. Keep the fragment in step with
    // backend/internal/domain/rag.go if either side is reworded.
    pattern: 'conversation is too large',
    code: 'CONTEXT_LIMIT',
    message: 'This conversation is too large to process. Start a new chat, or remove an attached file, and try again.'
  },
  {
    // Matches domain.ErrAttachmentsExceedContext on the backend, which the
    // agent SSE stream sends verbatim. That message also includes a
    // per-attachment row/character count (see attachmentSummary in
    // backend/internal/domain/rag.go), which can itself contain a 3-digit
    // number in the 400-599 range. This pattern is checked, and its own code
    // kept, ahead of the bare status-number patterns below for that reason.
    // Keep the fragment in step with backend/internal/domain/rag.go if either
    // side is reworded.
    pattern: 'exceed the model context window',
    code: 'ATTACHMENTS_CONTEXT_LIMIT',
    message:
      'One or more attached files are too large to process. Remove an attachment, or start a new chat, and try again.'
  },
  {
    pattern: 'Failed to fetch',
    code: 'NETWORK',
    message: 'Unable to reach the server. Please check your connection and try again.'
  },
  {
    pattern: 'NetworkError',
    code: 'NETWORK',
    message: 'Unable to reach the server. Please check your connection and try again.'
  },
  { pattern: 'Failed to save message', code: 'SAVE', message: 'Your message could not be saved. Please try again.' }
]

// An HTTP status only ever reaches this function inside the string
// `Request failed: <status>`, built in ssEventStream.ts when a fetch returns a
// non-ok response. Anchoring on that prefix is what stops an unrelated 3-digit
// number in a backend message, an attachment row count or a character budget,
// from being promoted to the displayed error code. The previous bare '400',
// '500' and '504' patterns matched anywhere in the string, so any message
// carrying such a number was one reword away from rendering as a server fault.
const statusPattern = /Request failed: ([45]\d{2})\b/

const statusMessages: Record<string, string> = {
  '400': 'There was a problem with the request. Please try again.',
  '500': 'Something went wrong on our end. Please try again.',
  '504': 'The request timed out. Please try again.'
}

const statusClassFallbacks: Record<string, string> = {
  '4': 'There was a problem with the request. Please try again.',
  '5': 'Something went wrong on our end. Please try again.'
}

export interface MappedError {
  code: string
  message: string
}

// Proposal-scoped override for the context-window case above. The classifier and the fragment are
// shared with chat (see backend/internal/domain/rag.go and rag_llm_error_test.go), but a proposal
// has neither threads nor attachments to act on, only pinned sources, so the chat copy's
// remediation clause ("Start a new thread, or remove an attached file") is not true here. Reword
// the copy; keep the fragment so this still matches the backend string unchanged.
const proposalErrorMap: Array<{ pattern: string; code: string; message: string }> = [
  {
    pattern: 'conversation is too large',
    code: 'CONTEXT_LIMIT',
    message:
      'This section is too large to process. Remove a pinned source, or split it into a smaller file, and try again.'
  }
]

// mapProposalErrorMessage rewords the one chat-shaped remediation clause that does not apply to a
// proposal. Everything else a proposal SSE error frame carries (a template-parse failure, "Add
// source content before building", "Generation failed", and so on) is already a proposal-specific
// message composed by the backend or the handler, so it must reach the user unchanged. That is why
// an unmatched message here passes through verbatim rather than collapsing to mapErrorMessage's
// generic "Something went wrong" fallback, which is tuned for raw fetch/network failures rather
// than these already-composed strings.
export function mapProposalErrorMessage(raw: string): MappedError {
  for (const { pattern, code, message } of proposalErrorMap) {
    if (raw.includes(pattern)) return { code, message }
  }
  return { code: 'UNKNOWN', message: raw }
}

export function mapErrorMessage(raw: string): MappedError {
  // Semantic patterns first. Each carries its own actionable code, which wins
  // outright, so a number appearing anywhere in the message cannot displace it.
  for (const { pattern, code, message } of errorMap) {
    if (raw.includes(pattern)) return { code, message }
  }

  // Then the anchored status, if this really is an HTTP failure.
  const statusCode = raw.match(statusPattern)?.[1]
  if (statusCode) {
    const exact = statusMessages[statusCode]
    if (exact) return { code: statusCode, message: exact }
    // Unlisted status: fall back on its class (4xx client, 5xx server).
    const classMessage = statusClassFallbacks[statusCode.charAt(0)]
    if (classMessage) return { code: statusCode, message: classMessage }
  }

  return { code: 'UNKNOWN', message: 'Something went wrong. Please try again.' }
}

// failureLine picks the line shown for a failed action: the thrown Error's own
// message when it carries one worth showing, otherwise the caller's fallback.
// Shared by every canvas action path so one convention governs what a user
// reads after a failure.
export function failureLine(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback
}
